import { Modes } from "../../Util/Music/Modes"
import { Music } from "../../Util/Music/Music"
import { HexMap } from "./HexMap"
import { HexMapBuilderParams } from "./HexMapBuilder"
import { Key } from "./Key"
import { KeyRelationship, Note, NoteParams } from "./Note"

export class HexMapNoteBuilder{
    hexMap:HexMap
    builderParams:HexMapBuilderParams
    constructor(hexMap:HexMap, builderParams:HexMapBuilderParams){
        this.hexMap = hexMap
        this.builderParams = builderParams
    }
    buildNotes = () => {
        const keys:Key[] = this.hexMap.keys.flat()
        keys.forEach((key:Key)=>{
            this.buildNotesForKey(key)
        })
        
    }

    buildNotesForKey = (key:Key) => {
        this.buildIonian(key)
        this.buildDorian(key)
        this.buildPhrygian(key)
        this.buildLydian(key)
        this.buildMixolydian(key)
        this.buildAeolian(key)
        this.buildLocrian(key)
    }
    buildNote = (x:number, y:number, tonic:number) => {
        const noteIndex = this.hexMap.notes.length
        const noteParams:NoteParams = {
            noteIndex: noteIndex,
            x: x,
            y: y,
            tonic: tonic
        }
        const note:Note = new Note(noteParams)
        return note
    }
    bondKeyAndNote = (key:Key, note:Note, position:number) => {
        const keyRelationship:KeyRelationship = {
            keyIndex: key.params.keyIndex,
            position: position,
            label: Modes.Names[position],
            labelDisplay: false,
            petalDisplay: false
        }
        note.keys.push(keyRelationship) // keyindex and position
        key.noteIndexes[position] = note.params.noteIndex
    }
    buildIonian = (key:Key) => {        
        const flatSixthKey:Key | null = this.getFlatSixthKey(key.params.rowIndex, key.params.columnIndex)
        const fourthKey:Key | null = this.getFourthKey(key.params.rowIndex, key.params.columnIndex)
        if(flatSixthKey){
            const noteIndex:number = flatSixthKey.noteIndexes[2]
            const note:Note = this.hexMap.notes[noteIndex]
            this.bondKeyAndNote(key, note, 0)
        }
        else if(fourthKey){
            const noteIndex:number = fourthKey.noteIndexes[4]
            const note:Note = this.hexMap.notes[noteIndex]
            this.bondKeyAndNote(key, note, 0)
        }
        
        else{
            const x = key.params.x
            const y = key.params.y - this.builderParams.radius
            const tonic = key.params.tonic
            const note = this.buildNote(x, y, tonic)
            this.bondKeyAndNote(key, note, 0)
            this.hexMap.notes.push(note)
        }
    }
    buildDorian = (key:Key) => {
        const fourthKey:Key | null = this.getFourthKey(key.params.rowIndex, key.params.columnIndex)

        if(fourthKey){
            const noteIndex:number = fourthKey.noteIndexes[5]
            const note:Note = this.hexMap.notes[noteIndex]
            if(key.params.rowIndex % 2 === 0 && key.params.rowIndex > 0){
            }
            this.bondKeyAndNote(key, note, 1)
        }
        else{
            const x = key.params.x + Math.floor(this.builderParams.radius * HexMap.NoteXFactor)
            const y = key.params.y - Math.floor(this.builderParams.radius * HexMap.NoteYFactor)
            const tonic = Music.addInterval(key.params.tonic, Music.interval_2nd)
            const note = this.buildNote(x, y, tonic)
            this.bondKeyAndNote(key, note, 1)
            this.hexMap.notes.push(note)
        }

    }
    buildPhrygian = (key:Key) => {
        const x = key.params.x + Math.floor(this.builderParams.radius * HexMap.NoteXFactor)
        const y = key.params.y + Math.floor(this.builderParams.radius * HexMap.NoteYFactor)
        const tonic = Music.addInterval(key.params.tonic, Music.interval_3rd)
        const note = this.buildNote(x, y, tonic)
        this.bondKeyAndNote(key, note, 2)
        this.hexMap.notes.push(note)    
    }
    buildLydian = (key:Key) => {
        const flatSixthKey:Key | null = this.getFlatSixthKey(key.params.rowIndex, key.params.columnIndex)
        const prevKey:Key | null = this.getFlatThirdKey(key.params.rowIndex, key.params.columnIndex)
        if(flatSixthKey){
            const noteIndex:number = flatSixthKey.noteIndexes[5]
            const note:Note = this.hexMap.notes[noteIndex]
            this.bondKeyAndNote(key, note, 3)
        }
        else if(prevKey){
            const noteIndex:number = prevKey.noteIndexes[1]
            const note:Note = this.hexMap.notes[noteIndex]
            this.bondKeyAndNote(key, note, 3)
        }
        else{
            const x = key.params.x - Math.floor(this.builderParams.radius * HexMap.NoteXFactor)
            const y = key.params.y - Math.floor(this.builderParams.radius * HexMap.NoteYFactor)
            const tonic = Music.addInterval(key.params.tonic, Music.interval_4th)
            const note = this.buildNote(x, y, tonic)
            this.bondKeyAndNote(key, note, 3)
            this.hexMap.notes.push(note)
        }
    }
    buildMixolydian = (key:Key) => {
        const prevKey:Key | null = this.getFlatThirdKey(key.params.rowIndex, key.params.columnIndex)
        if(prevKey){
            const noteIndex:number = prevKey.noteIndexes[2]
            const note:Note = this.hexMap.notes[noteIndex]
            this.bondKeyAndNote(key, note, 4)
        }
        else{ 
            const x = key.params.x - Math.floor(this.builderParams.radius * HexMap.NoteXFactor)
            const y = key.params.y + Math.floor(this.builderParams.radius * HexMap.NoteYFactor)
            const tonic = Music.addInterval(key.params.tonic, Music.interval_5th)
            const note = this.buildNote(x, y, tonic)
            this.bondKeyAndNote(key, note, 4)
            this.hexMap.notes.push(note)
        }
    }
    buildAeolian = (key:Key) => {
        const x = key.params.x
        const y = key.params.y + this.builderParams.radius
        const tonic = Music.addInterval(key.params.tonic, Music.interval_6th)
        const note = this.buildNote(x, y, tonic)
        this.bondKeyAndNote(key, note, 5)
        this.hexMap.notes.push(note)
    }
    buildLocrian = (key:Key) => {
        const x = key.params.x
        const y = key.params.y
        const tonic = Music.addInterval(key.params.tonic, Music.interval_7th)
        const note = this.buildNote(x, y, tonic)
        this.bondKeyAndNote(key, note, 6)
        this.hexMap.notes.push(note)
    }
    getFlatSixthKey = (rowIndex:number, columnIndex:number) =>{
        if(rowIndex === 0){ // first row
            return null
        }
        if(rowIndex % 2 === 0){ // even row 0,2,4
            if(columnIndex === 0){ // first key
                return null
            }
            else{
                return this.hexMap.keys[rowIndex-1][columnIndex-1]
            } 
        }
        else{ // odd row  1,3,5
            return this.hexMap.keys[rowIndex-1][columnIndex]
        }
    }
    getFourthKey = (rowIndex:number, columnIndex:number) =>{
        if(rowIndex === 0){ // first row
            return null
        }
        if(rowIndex % 2 === 0){ // even row
            return this.hexMap.keys[rowIndex-1][columnIndex]
        }
        else{ // odd row
            if(columnIndex === this.builderParams.columns - 1){ //last
                return null
            }
            return this.hexMap.keys[rowIndex-1][columnIndex + 1]
        }
    }
    getFlatThirdKey = (rowIndex:number, columnIndex:number) => {
        if(columnIndex === 0){
            return null
        }
        return this.hexMap.keys[rowIndex][columnIndex - 1]
    }
}